import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react"
import { useEffect, useRef, useState } from "react"

interface TopicSuggestionsModalProps {
  isOpen: boolean
  onClose: () => void
  suggestedTopics: string[]
  isLoading: boolean
  onAccept: (topics: string[]) => void
  onRegenerate: () => void
  provider?: string
  model?: string
  estimatedCost?: number
}

export function TopicSuggestionsModal({
  isOpen,
  onClose,
  suggestedTopics,
  isLoading,
  onAccept,
  onRegenerate,
  provider,
  model,
  estimatedCost,
}: TopicSuggestionsModalProps) {
  // Track which topics are selected (all selected by default)
  const [selectedTopics, setSelectedTopics] = useState<string[]>([])

  const previousSuggestionsRef = useRef<string[]>([])
  const wasOpenRef = useRef(false)

  // Initialize selected topics when modal opens or suggestions truly change
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false
      return
    }

    if (suggestedTopics.length === 0) {
      setSelectedTopics([])
      previousSuggestionsRef.current = []
      wasOpenRef.current = true
      return
    }

    const previousSuggestions = previousSuggestionsRef.current
    const isNewOpen = !wasOpenRef.current && isOpen
    const suggestionsChanged =
      previousSuggestions.length !== suggestedTopics.length ||
      previousSuggestions.some(
        (topic, index) => topic !== suggestedTopics[index],
      )

    if (isNewOpen || suggestionsChanged) {
      setSelectedTopics(suggestedTopics)
      previousSuggestionsRef.current = suggestedTopics
    }

    wasOpenRef.current = true
  }, [isOpen, suggestedTopics])

  const handleTopicToggle = (topic: string, shouldSelect: boolean) => {
    setSelectedTopics((prev) => {
      if (shouldSelect) {
        return prev.includes(topic) ? prev : [...prev, topic]
      }
      return prev.filter((t) => t !== topic)
    })
  }

  const handleAccept = () => {
    onAccept(selectedTopics)
  }

  const getCheckboxId = (topic: string) =>
    `topic-${topic.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`

  const selectedCount = selectedTopics.length

  console.log("=== TopicSuggestionsModal Debug ===")
  console.log("isOpen:", isOpen)
  console.log("suggestedTopics:", suggestedTopics)
  console.log("selectedTopics:", selectedTopics)
  console.log("selectedCount:", selectedCount)

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
      size={{ base: "sm", md: "lg" }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>AI Suggested Topics</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <HStack justify="center" py={6}>
              <Spinner size="lg" />
              <Text>Analyzing question...</Text>
            </HStack>
          ) : (
            <VStack align="stretch" gap={4}>
              <Text fontSize="sm" color="gray.600">
                Select the topics you want to add to this question:
              </Text>

              <VStack align="stretch" gap={2}>
                {suggestedTopics.map((topic) => {
                  const isChecked = selectedTopics.includes(topic)
                  const checkboxId = getCheckboxId(topic)
                  return (
                    <label
                      key={topic}
                      htmlFor={checkboxId}
                      style={{
                        cursor: "pointer",
                        padding: "0.5rem",
                        borderRadius: "0.375rem",
                        display: "flex",
                        gap: "0.75rem",
                        alignItems: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#F7FAFC"
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent"
                      }}
                    >
                      <input
                        id={checkboxId}
                        type="checkbox"
                        checked={isChecked}
                        onChange={(event) => {
                          console.log(
                            "Checkbox onChange:",
                            topic,
                            event.target.checked,
                          )
                          handleTopicToggle(topic, event.target.checked)
                        }}
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                        }}
                      />
                      <Text fontWeight="medium">{topic}</Text>
                    </label>
                  )
                })}
              </VStack>

              {provider && model && (
                <Text fontSize="xs" color="gray.500">
                  Generated by {provider} ({model})
                  {estimatedCost !== undefined &&
                    estimatedCost !== null &&
                    ` • Est. cost: $${estimatedCost.toFixed(6)}`}
                </Text>
              )}

              <Text fontSize="xs" color="gray.600" mt={2}>
                {selectedCount > 0 ? (
                  <>
                    {selectedCount} topic{selectedCount === 1 ? "" : "s"}{" "}
                    selected. New topics will be created automatically if they
                    don't exist in your organization.
                  </>
                ) : (
                  "No topics selected. Select at least one topic to continue."
                )}{" "}
              </Text>
            </VStack>
          )}
        </DialogBody>

        <DialogFooter gap={2}>
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="outline" onClick={onRegenerate} disabled={isLoading}>
            Regenerate
          </Button>
          <Button
            colorPalette="blue"
            onClick={handleAccept}
            disabled={isLoading || selectedCount === 0}
          >
            {`Add Selected Topic${
              selectedCount === 1 ? "" : "s"
            } (${selectedCount})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
